import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';
import { useGetLegalTransactionTypes } from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetUsers } from '../hooks/reactQuerys/UserQueries';
import { useCourts } from '../Aemter/hooks';

interface IReportConfig {
  id: string;
  title: string;
  description: string;
  defaultChart: 'bar' | 'pie' | 'line';
  supportedCharts: Array<'bar' | 'pie' | 'line'>;
  // Welche Filter dieser Report unterstützt — bestimmt, welche Selektoren
  // die UI ein-/ausblendet.
  filters: Array<
    | 'from'
    | 'to'
    | 'grouping'
    | 'employee'
    | 'transactionType'
    | 'court'
    | 'daysWindow'
  >;
}

interface IReportRow {
  label: string;
  value?: number;
  count?: number;
  sum?: number;
  samples?: number;
  /** Bewertungs-Reports: Durchschnittsscore in der Periode/Gruppe. */
  avgScore?: number;
  /** Bewertungs-Reports: Gesamtzahl versendeter Aufforderungen
   *  (fuer Antwortquote-Berechnung). */
  total?: number;
}

interface IReportData {
  items: IReportRow[];
  totals?: { count?: number; sum?: number };
  grouping?: string;
}

interface IReportFilters {
  fromDate?: string;
  toDate?: string;
  grouping?: 'day' | 'week' | 'month';
  idEmployee?: number | null;
  idLegalTransactionType?: number | null;
  idCourt?: number | null;
  daysWindow?: number | null;
  chartType?: 'bar' | 'pie' | 'line';
}

interface ISavedReport {
  idSavedReport: number;
  Title: string;
  ReportId: string;
  Filters: IReportFilters;
}

const PIE_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#c2185b',
  '#7b1fa2',
  '#0097a7',
  '#5d4037',
  '#455a64',
];

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(n);

const chartLabel: Record<'bar' | 'pie' | 'line', string> = {
  bar: 'Balken',
  pie: 'Kreis',
  line: 'Linie',
};

/**
 * Reports / Business-Intelligence-View — fest verdrahtete Reports aus dem
 * Backend (siehe app/logic/reports.py). Filter werden zentral in einem
 * `filters`-State verwaltet, damit Save/Load trivial ist.
 */
export const ReportsAnalyticsView: React.FC = () => {
  const queryClient = useQueryClient();

  // -------- Stammdaten für Filter --------
  const { data: configList, isLoading: isLoadingConfigs } = useQuery<{
    items: IReportConfig[];
  }>({
    refetchOnWindowFocus: false,
    queryKey: ['/reports'],
    queryFn: () => customFetcher({ endpoint: '/reports' }),
  });

  const { legalTransactionTypes } = useGetLegalTransactionTypes();
  const { users } = useGetUsers();
  const { courts } = useCourts();

  // -------- Auswahl + Filterzustand --------
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [filters, setFilters] = useState<IReportFilters>({
    fromDate: '',
    toDate: '',
    grouping: 'month',
    chartType: 'bar',
  });

  const selectedReport = useMemo(
    () => configList?.items.find(r => r.id === selectedReportId) ?? null,
    [configList, selectedReportId]
  );

  // Beim ersten Laden den ersten Report vorauswählen.
  useEffect(() => {
    if (!selectedReportId && configList?.items?.length) {
      const first = configList.items[0];
      setSelectedReportId(first.id);
      setFilters(prev => ({ ...prev, chartType: first.defaultChart }));
    }
  }, [configList, selectedReportId]);

  // Wenn der ausgewählte Report den aktuellen Charttyp nicht unterstützt,
  // fallback auf den Standardchart des Reports.
  useEffect(() => {
    if (!selectedReport) return;
    if (
      filters.chartType &&
      !selectedReport.supportedCharts.includes(filters.chartType)
    ) {
      setFilters(prev => ({ ...prev, chartType: selectedReport.defaultChart }));
    }
  }, [selectedReport, filters.chartType]);

  const supports = useCallback(
    (
      f:
        | 'from'
        | 'to'
        | 'grouping'
        | 'employee'
        | 'transactionType'
        | 'court'
        | 'daysWindow'
    ) => !!selectedReport?.filters.includes(f),
    [selectedReport]
  );

  // -------- Report-Daten ziehen --------
  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filters.fromDate) p.fromDate = filters.fromDate;
    if (filters.toDate) p.toDate = filters.toDate;
    if (supports('grouping') && filters.grouping) p.grouping = filters.grouping;
    if (supports('employee') && filters.idEmployee != null)
      p.idEmployee = String(filters.idEmployee);
    if (supports('transactionType') && filters.idLegalTransactionType != null)
      p.idLegalTransactionType = String(filters.idLegalTransactionType);
    if (supports('court') && filters.idCourt != null)
      p.idCourt = String(filters.idCourt);
    if (supports('daysWindow') && filters.daysWindow != null)
      p.daysWindow = String(filters.daysWindow);
    return p;
  }, [filters, supports]);

  const queryString = useMemo(() => {
    const qs = new URLSearchParams(queryParams).toString();
    return qs ? `?${qs}` : '';
  }, [queryParams]);

  const {
    data: reportData,
    isLoading: isLoadingData,
    error: reportError,
    refetch,
  } = useQuery<IReportData>({
    enabled: !!selectedReportId,
    refetchOnWindowFocus: false,
    queryKey: ['/reports/run', selectedReportId, queryString],
    queryFn: () =>
      customFetcher({
        endpoint: `/reports/${selectedReportId}${queryString}`,
      }),
  });

  // -------- Gespeicherte Reports --------
  const { data: savedReports } = useQuery<ISavedReport[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/savedreport'],
    queryFn: () => customFetcher({ endpoint: '/savedreport' }),
  });

  const invalidateSaved = () =>
    queryClient.invalidateQueries({ queryKey: ['/savedreport'] });

  const { mutateAsync: saveReport, isPending: isSaving } = useMutation({
    mutationFn: (input: {
      Title: string;
      ReportId: string;
      Filters: IReportFilters;
    }) =>
      customFetcher({
        endpoint: '/savedreport',
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidateSaved,
  });

  const { mutateAsync: deleteSavedReport } = useMutation({
    mutationFn: (idSavedReport: number) =>
      customFetcher({
        endpoint: `/savedreport/${idSavedReport}`,
        method: 'DELETE',
      }),
    onSuccess: invalidateSaved,
  });

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');

  const handleSelectReport = (
    id: string,
    defaultChart: 'bar' | 'pie' | 'line'
  ) => {
    setSelectedReportId(id);
    // Filter-Reset auf vernünftige Defaults beim Wechsel zwischen Reports —
    // Datum bleibt, weil Zeitraum meist gleich relevant ist.
    setFilters(prev => ({
      fromDate: prev.fromDate,
      toDate: prev.toDate,
      grouping: 'month',
      chartType: defaultChart,
    }));
  };

  const handleLoadSaved = (s: ISavedReport) => {
    setSelectedReportId(s.ReportId);
    setFilters({
      fromDate: '',
      toDate: '',
      grouping: 'month',
      chartType: 'bar',
      ...(s.Filters || {}),
    });
  };

  const handleSave = async () => {
    if (!selectedReportId || !saveTitle.trim()) return;
    await saveReport({
      Title: saveTitle.trim(),
      ReportId: selectedReportId,
      Filters: filters,
    });
    setSaveDialogOpen(false);
    setSaveTitle('');
  };

  const handleDeleteSaved = async (s: ISavedReport) => {
    if (
      !window.confirm(`Gespeicherten Report „${s.Title}" wirklich löschen?`)
    ) {
      return;
    }
    await deleteSavedReport(s.idSavedReport);
  };

  // -------- Export --------
  const downloadFile = useCallback(
    (content: string, filename: string, mime: string) => {
      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    []
  );

  const exportJson = useCallback(() => {
    if (!selectedReport || !reportData) return;
    const payload = {
      report: {
        id: selectedReport.id,
        title: selectedReport.title,
        chartType: filters.chartType,
      },
      filters,
      generatedAt: new Date().toISOString(),
      data: reportData,
    };
    downloadFile(
      JSON.stringify(payload, null, 2),
      `${selectedReport.id}-${new Date().toISOString().slice(0, 10)}.json`,
      'application/json'
    );
  }, [selectedReport, reportData, filters, downloadFile]);

  const exportCsv = useCallback(() => {
    if (!selectedReport || !reportData?.items?.length) return;
    const cols = Object.keys(reportData.items[0]);
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      cols.join(';'),
      ...reportData.items.map(row =>
        cols
          .map(c => escape((row as unknown as Record<string, unknown>)[c]))
          .join(';')
      ),
    ];
    downloadFile(
      lines.join('\n'),
      `${selectedReport.id}-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv'
    );
  }, [selectedReport, reportData, downloadFile]);

  // -------- Chart --------
  const renderChart = () => {
    if (!reportData?.items?.length) {
      return (
        <Alert severity='info' sx={{ mt: 2 }}>
          Keine Daten für die gewählten Filter.
        </Alert>
      );
    }
    const data = reportData.items.map(d => ({
      ...d,
      value:
        d.value != null ? Number(d.value) : d.sum != null ? Number(d.sum) : 0,
    }));
    const ct = filters.chartType || 'bar';

    if (ct === 'pie') {
      return (
        <ResponsiveContainer width='100%' height={360}>
          <PieChart>
            <Pie
              data={data}
              dataKey='value'
              nameKey='label'
              cx='50%'
              cy='50%'
              outerRadius={120}
              label
            >
              {data.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Legend />
            <RechartsTooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (ct === 'line') {
      return (
        <ResponsiveContainer width='100%' height={360}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='label' />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line
              type='monotone'
              dataKey='value'
              stroke='#1976d2'
              strokeWidth={2}
              dot
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width='100%' height={360}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='label' />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Bar dataKey='value' fill='#1976d2' />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // -------- Aktive Filter als Chips für visuellen Feedback --------
  const activeFilterChips = (() => {
    const chips: Array<{ label: string; onDelete: () => void }> = [];
    if (filters.fromDate)
      chips.push({
        label: `Von: ${filters.fromDate}`,
        onDelete: () => setFilters(p => ({ ...p, fromDate: '' })),
      });
    if (filters.toDate)
      chips.push({
        label: `Bis: ${filters.toDate}`,
        onDelete: () => setFilters(p => ({ ...p, toDate: '' })),
      });
    if (supports('employee') && filters.idEmployee != null) {
      const u = users?.find(x => x.idUser === filters.idEmployee);
      chips.push({
        label: `Mitarbeiter: ${u ? `${u.FirstName} ${u.LastName}` : filters.idEmployee}`,
        onDelete: () => setFilters(p => ({ ...p, idEmployee: null })),
      });
    }
    if (supports('transactionType') && filters.idLegalTransactionType != null) {
      const t = legalTransactionTypes?.find(
        x => x.idLegalTransactionType === filters.idLegalTransactionType
      );
      chips.push({
        label: `Rechtsgeschäftsart: ${t?.LegalTransactionType ?? filters.idLegalTransactionType}`,
        onDelete: () =>
          setFilters(p => ({ ...p, idLegalTransactionType: null })),
      });
    }
    if (supports('court') && filters.idCourt != null) {
      const c = courts?.find(x => x.idCourt === filters.idCourt);
      chips.push({
        label: `Gericht: ${c?.Court ?? filters.idCourt}`,
        onDelete: () => setFilters(p => ({ ...p, idCourt: null })),
      });
    }
    return chips;
  })();

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper variant='outlined' sx={{ p: 1, mb: 2 }}>
            <Typography variant='subtitle2' sx={{ px: 1, py: 0.5 }}>
              Verfügbare Reports
            </Typography>
            <Divider />
            {isLoadingConfigs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <List
                dense
                disablePadding
                sx={{ maxHeight: 420, overflowY: 'auto' }}
              >
                {(configList?.items || []).map(r => (
                  <ListItemButton
                    key={r.id}
                    selected={r.id === selectedReportId}
                    onClick={() => handleSelectReport(r.id, r.defaultChart)}
                  >
                    <ListItemText
                      primary={r.title}
                      secondary={r.description}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          <Paper variant='outlined' sx={{ p: 1 }}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{ px: 1, py: 0.5 }}
            >
              <BookmarksIcon fontSize='small' color='action' />
              <Typography variant='subtitle2'>Meine Reports</Typography>
            </Stack>
            <Divider />
            {!savedReports?.length ? (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', p: 1.5 }}
              >
                Noch keine gespeicherten Reports. Stellen Sie einen Report mit
                gewünschten Filtern zusammen und nutzen Sie „Speichern".
              </Typography>
            ) : (
              <List
                dense
                disablePadding
                sx={{ maxHeight: 320, overflowY: 'auto' }}
              >
                {savedReports.map(s => (
                  <ListItemButton
                    key={s.idSavedReport}
                    onClick={() => handleLoadSaved(s)}
                  >
                    <ListItemText
                      primary={s.Title}
                      secondary={
                        configList?.items.find(c => c.id === s.ReportId)
                          ?.title || s.ReportId
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title='Löschen'>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={e => {
                            e.stopPropagation();
                            void handleDeleteSaved(s);
                          }}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card variant='outlined'>
            <CardContent>
              {!selectedReport ? (
                <Alert severity='info'>
                  Wählen Sie links einen Report aus.
                </Alert>
              ) : (
                <>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    flexWrap='wrap'
                    spacing={1}
                  >
                    <Box>
                      <Typography variant='h6' sx={{ mb: 0.5 }}>
                        {selectedReport.title}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block' }}
                      >
                        {selectedReport.description}
                      </Typography>
                    </Box>
                    <Stack direction='row' spacing={0.5}>
                      <Tooltip title='Aktualisieren'>
                        <IconButton
                          size='small'
                          onClick={() => refetch()}
                          disabled={isLoadingData}
                        >
                          <RefreshIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Aktuelle Konfiguration speichern'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() => setSaveDialogOpen(true)}
                            disabled={isSaving}
                          >
                            <SaveIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Als JSON exportieren'>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={exportJson}
                            disabled={!reportData?.items?.length}
                            startIcon={<DownloadIcon fontSize='small' />}
                          >
                            JSON
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title='Als CSV exportieren'>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={exportCsv}
                            disabled={!reportData?.items?.length}
                            startIcon={<DownloadIcon fontSize='small' />}
                          >
                            CSV
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={1.5}>
                    {supports('from') && (
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label='Von'
                          type='date'
                          size='small'
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={filters.fromDate ?? ''}
                          onChange={e =>
                            setFilters(p => ({
                              ...p,
                              fromDate: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                    )}
                    {supports('to') && (
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label='Bis'
                          type='date'
                          size='small'
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={filters.toDate ?? ''}
                          onChange={e =>
                            setFilters(p => ({ ...p, toDate: e.target.value }))
                          }
                        />
                      </Grid>
                    )}
                    {supports('grouping') && (
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='Gruppierung'
                          select
                          size='small'
                          fullWidth
                          value={filters.grouping ?? 'month'}
                          onChange={e =>
                            setFilters(p => ({
                              ...p,
                              grouping: e.target.value as
                                | 'day'
                                | 'week'
                                | 'month',
                            }))
                          }
                        >
                          <MenuItem value='day'>Tag</MenuItem>
                          <MenuItem value='week'>Woche</MenuItem>
                          <MenuItem value='month'>Monat</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    {supports('daysWindow') && (
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label='Zeitfenster (Tage)'
                          type='number'
                          size='small'
                          fullWidth
                          value={filters.daysWindow ?? 30}
                          onChange={e =>
                            setFilters(p => ({
                              ...p,
                              daysWindow:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            }))
                          }
                        />
                      </Grid>
                    )}
                    {supports('employee') && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          size='small'
                          options={users || []}
                          getOptionLabel={u => `${u.FirstName} ${u.LastName}`}
                          isOptionEqualToValue={(o, v) => o.idUser === v.idUser}
                          value={
                            users?.find(u => u.idUser === filters.idEmployee) ??
                            null
                          }
                          onChange={(_, v) =>
                            setFilters(p => ({
                              ...p,
                              idEmployee: v ? v.idUser : null,
                            }))
                          }
                          renderInput={params => (
                            <TextField {...params} label='Mitarbeiter' />
                          )}
                        />
                      </Grid>
                    )}
                    {supports('transactionType') && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          size='small'
                          options={legalTransactionTypes || []}
                          getOptionLabel={t => t.LegalTransactionType}
                          isOptionEqualToValue={(o, v) =>
                            o.idLegalTransactionType ===
                            v.idLegalTransactionType
                          }
                          value={
                            legalTransactionTypes?.find(
                              t =>
                                t.idLegalTransactionType ===
                                filters.idLegalTransactionType
                            ) ?? null
                          }
                          onChange={(_, v) =>
                            setFilters(p => ({
                              ...p,
                              idLegalTransactionType: v
                                ? v.idLegalTransactionType
                                : null,
                            }))
                          }
                          renderInput={params => (
                            <TextField {...params} label='Rechtsgeschäftsart' />
                          )}
                        />
                      </Grid>
                    )}
                    {supports('court') && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                          size='small'
                          options={courts || []}
                          getOptionLabel={c => c.Court}
                          isOptionEqualToValue={(o, v) =>
                            o.idCourt === v.idCourt
                          }
                          value={
                            courts?.find(c => c.idCourt === filters.idCourt) ??
                            null
                          }
                          onChange={(_, v) =>
                            setFilters(p => ({
                              ...p,
                              idCourt: v ? v.idCourt : null,
                            }))
                          }
                          renderInput={params => (
                            <TextField {...params} label='Gericht' />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label='Diagramm'
                        select
                        size='small'
                        fullWidth
                        value={filters.chartType ?? 'bar'}
                        onChange={e =>
                          setFilters(p => ({
                            ...p,
                            chartType: e.target.value as 'bar' | 'pie' | 'line',
                          }))
                        }
                      >
                        {selectedReport.supportedCharts.map(c => (
                          <MenuItem key={c} value={c}>
                            {chartLabel[c]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {activeFilterChips.length > 0 && (
                    <Stack
                      direction='row'
                      spacing={0.5}
                      flexWrap='wrap'
                      useFlexGap
                      sx={{ mt: 1.5 }}
                    >
                      {activeFilterChips.map((c, i) => (
                        <Chip
                          key={i}
                          size='small'
                          label={c.label}
                          onDelete={c.onDelete}
                          variant='outlined'
                        />
                      ))}
                    </Stack>
                  )}

                  <Box sx={{ mt: 2 }}>
                    {reportError ? (
                      <Alert severity='error'>
                        {reportError instanceof Error
                          ? reportError.message
                          : 'Report konnte nicht geladen werden.'}
                      </Alert>
                    ) : isLoadingData ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 6,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        {renderChart()}
                        {reportData?.totals && (
                          <Stack
                            direction='row'
                            spacing={2}
                            sx={{ mt: 2 }}
                            flexWrap='wrap'
                          >
                            {reportData.totals.count != null && (
                              <Paper
                                variant='outlined'
                                sx={{ p: 1.5, minWidth: 160 }}
                              >
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  Gesamt-Anzahl
                                </Typography>
                                <Typography variant='h6'>
                                  {reportData.totals.count}
                                </Typography>
                              </Paper>
                            )}
                            {reportData.totals.sum != null && (
                              <Paper
                                variant='outlined'
                                sx={{ p: 1.5, minWidth: 160 }}
                              >
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  Gesamt-Volumen
                                </Typography>
                                <Typography variant='h6'>
                                  {formatCurrency(reportData.totals.sum)}
                                </Typography>
                              </Paper>
                            )}
                          </Stack>
                        )}
                        {reportData?.items?.length ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Datenpunkte: {reportData.items.length}
                            </Typography>
                          </Box>
                        ) : null}
                      </>
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Report speichern</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size='small'
            margin='dense'
            label='Titel'
            value={saveTitle}
            onChange={e => setSaveTitle(e.target.value)}
            placeholder='z. B. „Vorgänge 2026 — Frau Müller"'
          />
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 1 }}
          >
            Die aktuellen Filter werden mit dem Titel verknüpft und sind danach
            unter „Meine Reports" abrufbar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={!saveTitle.trim() || isSaving}
          >
            {isSaving ? 'Speichere…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
